import { useIsMobile } from '@bemor/web-kit';
import {
    Badge,
    DetailRow,
    DetailRowSkeleton,
    FieldGrid,
    ObjectRef,
    Section,
    Skeleton,
    StatusBadge,
    StoreName,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@bemor/web-kit/ui';

import { Link } from 'react-router-dom';

import type { Organization, Store } from '@/api/types';
import { useOne } from '@/lib/queries';

/**
 * BM-86 (design 0.8.0 `backoffice/merchant`): the organization a merchant belongs to, read-only for staff: its name and
 * the sibling merchants as object refs with their status. Absent (renders nothing) for a merchant made before the
 * backfill, which answers 404.
 */
export function OrganizationBlock({ merchantId }: { merchantId: string }) {
    const organization = useOne<Organization>(
        `/staff/merchants/${merchantId}/organization`,
    );
    const raw = organization.data?.data;
    // a 404 (a merchant made before the backfill) answers an errors body, not an organization
    const org = raw && Array.isArray(raw.merchants) ? raw : null;
    if (organization.isPending)
        return (
            <Section title="Organization" testId="merchant-organization">
                <DetailRowSkeleton />
                <DetailRowSkeleton />
            </Section>
        );
    if (organization.isError || !org) return null;
    const siblings = org.merchants.filter((m) => m.id !== merchantId);
    return (
        <Section title="Organization" testId="merchant-organization">
            <FieldGrid>
                <DetailRow label="Name" layout="stacked">
                    {/* BM-93 decision 10: up to the organization page */}
                    <ObjectRef
                        id={org.id}
                        label={org.name || 'Unnamed organization'}
                        href={`/organizations/${org.id}`}
                    />
                </DetailRow>
                <DetailRow label="Merchants" layout="stacked">
                    {siblings.length === 0 ? (
                        <span className="text-fg-2">No other merchants.</span>
                    ) : (
                        <ul className="m-0 flex list-none flex-col gap-1 p-0">
                            {siblings.map((m) => (
                                <li
                                    key={m.id}
                                    data-testid={`sibling-${m.id}`}
                                    className="flex min-w-0 items-center gap-2"
                                >
                                    <ObjectRef
                                        id={m.id}
                                        label={m.display_name || m.id}
                                        href={`/merchants/${m.id}`}
                                    />
                                    <StatusBadge
                                        status={m.status}
                                        domain="merchant"
                                        size="sm"
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </DetailRow>
            </FieldGrid>
        </Section>
    );
}

const siteOf = (url: string | null | undefined) =>
    url ? url.replace(/^https?:\/\//, '') : '—';

/**
 * BM-86 (design 0.8.0 `backoffice/merchant`): the merchant's stores, read-only for staff: a table of name, site, descriptor,
 * status and default; rows on a phone (name with the Default tag, the descriptor under it, the status aside).
 */
export function StoresBlock({ merchantId }: { merchantId: string }) {
    const stores = useOne<Store[]>(`/staff/merchants/${merchantId}/stores`);
    const phone = useIsMobile();
    const rows = Array.isArray(stores.data?.data) ? stores.data.data : [];
    return (
        <Section title="Stores" flush testId="merchant-stores">
            {stores.isPending ? (
                <div className="flex flex-col gap-2 px-5 py-4">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-4 w-64" />
                </div>
            ) : rows.length === 0 ? (
                <p className="text-fg-2 m-0 px-5 py-4 [font:var(--body-sm)]">
                    No stores.
                </p>
            ) : phone ? (
                <ul className="m-0 list-none p-0">
                    {rows.map((s) => (
                        <li
                            key={s.id}
                            data-testid={`store-${s.id}`}
                            className="border-border-1 flex items-start justify-between gap-3 border-t px-5 py-3 first:border-t-0"
                        >
                            <div className="flex min-w-0 flex-col gap-1">
                                <span className="inline-flex items-center gap-2">
                                    <StoreName store={s} />
                                    {s.is_default ? (
                                        <Badge
                                            variant="neutral"
                                            emphasis="outline"
                                            size="sm"
                                        >
                                            Default
                                        </Badge>
                                    ) : null}
                                </span>
                                {s.descriptor ? (
                                    <span className="text-fg-2 font-mono text-xs">
                                        {s.descriptor}
                                    </span>
                                ) : null}
                            </div>
                            <span className="flex items-center gap-2">
                                <StatusBadge
                                    status={s.status}
                                    domain="store"
                                    size="sm"
                                />
                                <Link
                                    to={`/stores/${s.id}`}
                                    className="text-xs underline"
                                >
                                    Open
                                </Link>
                            </span>
                        </li>
                    ))}
                </ul>
            ) : (
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Name</TableHead>
                            <TableHead>Site URL</TableHead>
                            <TableHead>Descriptor</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Default</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {rows.map((s) => (
                            <TableRow key={s.id} data-testid={`store-${s.id}`}>
                                <TableCell>
                                    <StoreName store={s} />
                                </TableCell>
                                <TableCell>
                                    <span className="block max-w-[260px] truncate">
                                        {siteOf(s.site_url)}
                                    </span>
                                </TableCell>
                                <TableCell className="font-mono text-xs">
                                    {s.descriptor ?? '—'}
                                </TableCell>
                                <TableCell>
                                    <span className="flex items-center gap-2">
                                        <StatusBadge
                                            status={s.status}
                                            domain="store"
                                            size="sm"
                                        />
                                        <Link
                                            to={`/stores/${s.id}`}
                                            className="text-xs underline"
                                        >
                                            Open
                                        </Link>
                                    </span>
                                </TableCell>
                                <TableCell>
                                    {s.is_default ? (
                                        <Badge
                                            variant="neutral"
                                            emphasis="outline"
                                            size="sm"
                                        >
                                            Default
                                        </Badge>
                                    ) : null}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Section>
    );
}
